import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, User, Mail, Phone, MapPin, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { institutionAPI } from '../lib/api';
import { toast } from 'sonner';

export default function InstitutionSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    institution_name: '',
    institution_code: '',
    contact_person_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    master_admin_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.institution_name.trim()) {
      newErrors.institution_name = 'Institution name is required';
    }
    
    if (!formData.institution_code.trim()) {
      newErrors.institution_code = 'Institution code is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.institution_code.toLowerCase())) {
      newErrors.institution_code = 'Code must contain only lowercase letters, numbers, and hyphens';
    } else if (formData.institution_code.length < 3) {
      newErrors.institution_code = 'Code must be at least 3 characters';
    }
    
    if (!formData.contact_person_name.trim()) {
      newErrors.contact_person_name = 'Contact person name is required';
    }
    
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }
    
    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Phone number is required';
    }
    
    if (!formData.master_admin_password) {
      newErrors.master_admin_password = 'Password is required';
    } else if (formData.master_admin_password.length < 8) {
      newErrors.master_admin_password = 'Password must be at least 8 characters with letters and numbers';
    }
    
    if (formData.master_admin_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        institution_name: formData.institution_name,
        institution_code: formData.institution_code.toLowerCase(),
        contact_person_name: formData.contact_person_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address: formData.address || null,
        master_admin_password: formData.master_admin_password,
      };
      
      const response = await institutionAPI.signup(payload);
      setSuccess(response.data);
      toast.success('Institution registered successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Registration failed. Please try again.');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl text-white">Registration Successful!</CardTitle>
            <CardDescription className="text-slate-400">
              Your institution has been registered with a 15-day free trial
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Institution Code:</span>
                <span className="text-white font-mono">{success.institution_code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Admin Email:</span>
                <span className="text-white">{success.master_admin_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Trial Ends:</span>
                <span className="text-amber-400">{new Date(success.trial_end_date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-400">Login URL:</span>
                <a href={success.login_url} className="text-cyan-400 break-all hover:underline" data-testid="signup-login-url">{success.login_url}</a>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <p className="font-medium mb-1">Important:</p>
                  <p>Save your institution code <span className="font-mono bg-slate-800 px-1 rounded">{success.institution_code}</span> - you&apos;ll need it to log in.</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/login')} 
              className="w-full bg-cyan-600 hover:bg-cyan-500"
              data-testid="goto-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-cyan-400" />
          </div>
          <CardTitle className="text-2xl text-white">Register Your Institution</CardTitle>
          <CardDescription className="text-slate-400">
            Start your 15-day free trial - No credit card required
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Institution Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white border-b border-slate-700 pb-2">Institution Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="institution_name" className="text-slate-300">Institution Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="institution_name"
                      name="institution_name"
                      value={formData.institution_name}
                      onChange={handleChange}
                      placeholder="City Medical Center"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      data-testid="institution-name-input"
                    />
                  </div>
                  {errors.institution_name && <p className="text-red-400 text-sm">{errors.institution_name}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="institution_code" className="text-slate-300">Institution Code * <span className="text-slate-500 text-xs">(subdomain)</span></Label>
                  <Input
                    id="institution_code"
                    name="institution_code"
                    value={formData.institution_code}
                    onChange={handleChange}
                    placeholder="city-medical"
                    className="bg-slate-900/50 border-slate-600 text-white"
                    data-testid="institution-code-input"
                  />
                  {errors.institution_code && <p className="text-red-400 text-sm">{errors.institution_code}</p>}
                  <p className="text-slate-500 text-xs">Your URL will be: {formData.institution_code || 'your-code'}.hmc-buddy.com</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address" className="text-slate-300">Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Healthcare Blvd, City, State"
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                    data-testid="address-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Contact Person */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white border-b border-slate-700 pb-2">Administrator Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person_name" className="text-slate-300">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="contact_person_name"
                      name="contact_person_name"
                      value={formData.contact_person_name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      data-testid="contact-name-input"
                    />
                  </div>
                  {errors.contact_person_name && <p className="text-red-400 text-sm">{errors.contact_person_name}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="text-slate-300">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  {errors.contact_phone && <p className="text-red-400 text-sm">{errors.contact_phone}</p>}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact_email" className="text-slate-300">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="admin@hospital.com"
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                    data-testid="contact-email-input"
                  />
                </div>
                {errors.contact_email && <p className="text-red-400 text-sm">{errors.contact_email}</p>}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="master_admin_password" className="text-slate-300">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="master_admin_password"
                      name="master_admin_password"
                      type="password"
                      value={formData.master_admin_password}
                      onChange={handleChange}
                      placeholder="Min 8 characters, letters + numbers"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      data-testid="password-input"
                    />
                  </div>
                  {errors.master_admin_password && <p className="text-red-400 text-sm">{errors.master_admin_password}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password" className="text-slate-300">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {errors.confirm_password && <p className="text-red-400 text-sm">{errors.confirm_password}</p>}
                </div>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h4 className="text-white font-medium mb-2">What&apos;s included in your trial:</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Full access to all features</li>
                <li>• Unlimited users during trial</li>
                <li>• Patient management & workflows</li>
                <li>• Billing & reporting tools</li>
              </ul>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-cyan-600 hover:bg-cyan-500 h-12 text-lg"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating your institution...' : 'Start Free Trial'}
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-800 px-2 text-slate-400">Or</span>
              </div>
            </div>
            
            <Button 
              type="button"
              variant="outline"
              className="w-full h-12 text-lg border-slate-600 hover:bg-slate-700"
              onClick={() => navigate('/pricing')}
            >
              Subscribe Now
            </Button>
            
            <p className="text-center text-slate-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-cyan-400 hover:text-cyan-300">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
